'use client';

import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { RichTextEditor } from '@/components/shared/lexical-editor';

interface ProjectOverviewData {
  executive_summary?: {
    paragraph_1?: string;
    paragraph_2?: string;
  };
  project_vision?: string;
  key_objectives?: string[];
  target_audience?: {
    primary?: {
      roles?: string[];
      description?: string;
    };
    secondary?: {
      roles?: string[];
      description?: string;
    };
  };
  success_metrics?: Array<{
    metric_name?: string;
    target_value?: string;
    measurement_window?: string;
    notes?: string;
  }>;
  scope?: {
    in_scope?: string[];
    out_of_scope?: string[];
  };
  timeline?: Array<{
    phase_name?: string;
    date_range?: string;
    focus?: string[];
  }>;
  budget_and_resources?: {
    team_size?: string;
    role_breakdown?: Array<{ role?: string; count?: number }>;
    year_1_budget?: string;
    budget_notes?: string;
  };
  risks_and_assumptions?: string[];
  assumptions?: string[];
  evidence_refs?: Array<{
    ref_type?: string;
    ref_id?: string;
    note?: string;
  }>;
}

interface ProjectOverviewContentProps {
  content: string;
  isEditing?: boolean;
  onContentChange?: (content: string) => void;
  editorKey?: number;
}

/**
 * Clean up any embedded JSON objects in markdown content
 */
function cleanEmbeddedJson(content: string): string {
  const jsonPattern = /\{\s*"[^"]+"\s*:\s*(?:"[^"]*"|[^}]+)\s*(?:,\s*"[^"]+"\s*:\s*(?:"[^"]*"|[^}]+)\s*)*\}/g;
  
  return content.replace(jsonPattern, (match) => {
    try {
      const obj = JSON.parse(match);
      const parts: string[] = [];
      if (obj.metric_name) {
        parts.push(`**${obj.metric_name}**`);
        if (obj.target_value) parts.push(`Target: ${obj.target_value}`);
        if (obj.measurement_window) parts.push(`Timeframe: ${obj.measurement_window}`);
        if (obj.notes) parts.push(`(${obj.notes})`);
        return parts.join(' — ');
      }
      if (obj.phase_name) {
        parts.push(`**${obj.phase_name}**`);
        if (obj.date_range) parts.push(`(${obj.date_range})`);
        if (obj.focus_bullets && Array.isArray(obj.focus_bullets)) {
          parts.push(`: ${obj.focus_bullets.join('; ')}`);
        }
        return parts.join(' ');
      }
      if (obj.role) return `${obj.count || 1} ${obj.role}`;
      if (obj.ref_type) return `[${obj.ref_type}] ${obj.ref_id || ''}${obj.note ? ` — ${obj.note}` : ''}`;
      const kvPairs = Object.entries(obj)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => {
          const key = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return Array.isArray(v) ? `${key}: ${v.join('; ')}` : `${key}: ${v}`;
        });
      return kvPairs.join(' — ');
    } catch {
      return match.replace(/^\{\s*/, '').replace(/\s*\}$/, '')
        .replace(/"([^"]+)"\s*:\s*"([^"]*)"/g, '$1: $2')
        .replace(/",\s*/g, '; ').replace(/"/g, '');
    }
  });
}

/**
 * Parse content string to structured data
 */
function parseContent(content: string): ProjectOverviewData | null {
  const trimmed = content.trim();
  if (trimmed.startsWith('#') || trimmed.startsWith('*') || trimmed.startsWith('-')) return null;
  try {
    const parsed = JSON.parse(content);
    if (parsed.executive_summary || parsed.project_vision || parsed.key_objectives) {
      return parsed as ProjectOverviewData;
    }
    return null;
  } catch {
    return extractFromMalformedJson(content);
  }
}

function extractFromMalformedJson(content: string): ProjectOverviewData | null {
  const data: ProjectOverviewData = {};
  const para1Match = content.match(/"paragraph_1"\s*:\s*"([^"]+)"/);
  const para2Match = content.match(/"paragraph_2"\s*:\s*"([^"]+)"/);
  if (para1Match || para2Match) {
    data.executive_summary = { paragraph_1: para1Match?.[1], paragraph_2: para2Match?.[1] };
  }
  const visionMatch = content.match(/"project_vision"\s*:\s*"([^"]+)"/);
  if (visionMatch) data.project_vision = visionMatch[1];
  const objectivesMatch = content.match(/"key_objectives"\s*:\s*\[([^\]]+)\]/);
  if (objectivesMatch) {
    const objectives = objectivesMatch[1].match(/"([^"]+)"/g);
    if (objectives) data.key_objectives = objectives.map(obj => obj.replace(/"/g, ''));
  }
  if (Object.keys(data).length === 0) return null;
  return data;
}

/**
 * Parse markdown into sections for styled rendering
 */
function parseMarkdownSections(content: string): Array<{ title: string; content: string }> {
  const sections: Array<{ title: string; content: string }> = [];
  const lines = content.split('\n');
  let currentTitle = '';
  let currentContent: string[] = [];
  for (const line of lines) {
    const h2Match = line.match(/^##\s+(.+)$/);
    if (h2Match) {
      if (currentTitle || currentContent.length > 0) {
        sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
      }
      currentTitle = h2Match[1].trim();
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  }
  if (currentTitle || currentContent.length > 0) {
    sections.push({ title: currentTitle, content: currentContent.join('\n').trim() });
  }
  return sections;
}

/**
 * Convert structured data or markdown to HTML for the Lexical editor
 */
function contentToHtml(content: string): string {
  const trimmed = content.trim();
  if (trimmed.startsWith('<')) {
    return content;
  }

  const data = parseContent(content);

  if (data) {
    const parts: string[] = [];
    if (data.executive_summary) {
      parts.push('<h2>Executive Summary</h2>');
      if (data.executive_summary.paragraph_1) parts.push(`<p>${data.executive_summary.paragraph_1}</p>`);
      if (data.executive_summary.paragraph_2) parts.push(`<p>${data.executive_summary.paragraph_2}</p>`);
    }
    if (data.project_vision) {
      parts.push('<h2>Project Vision</h2>');
      parts.push(`<p>${data.project_vision}</p>`);
    }
    if (data.key_objectives?.length) {
      parts.push('<h2>Key Objectives</h2>');
      parts.push('<ul>');
      data.key_objectives.forEach(o => parts.push(`<li>${o}</li>`));
      parts.push('</ul>');
    }
    if (data.target_audience) {
      parts.push('<h2>Target Audience</h2>');
      if (data.target_audience.primary) {
        parts.push(`<p><strong>Primary:</strong> ${data.target_audience.primary.roles?.join(', ') || ''}</p>`);
        if (data.target_audience.primary.description) parts.push(`<p>${data.target_audience.primary.description}</p>`);
      }
      if (data.target_audience.secondary) {
        parts.push(`<p><strong>Secondary:</strong> ${data.target_audience.secondary.roles?.join(', ') || ''}</p>`);
        if (data.target_audience.secondary.description) parts.push(`<p>${data.target_audience.secondary.description}</p>`);
      }
    }
    if (data.success_metrics?.length) {
      parts.push('<h2>Success Metrics</h2>');
      parts.push('<ol>');
      data.success_metrics.forEach(m => {
        parts.push(`<li><strong>${m.metric_name || 'Metric'}:</strong> ${m.target_value || 'TBD'}${m.measurement_window ? ` within ${m.measurement_window}` : ''}</li>`);
      });
      parts.push('</ol>');
    }
    if (data.scope) {
      parts.push('<h2>Scope</h2>');
      if (data.scope.in_scope?.length) {
        parts.push('<p><strong>In Scope:</strong></p><ul>');
        data.scope.in_scope.forEach(s => parts.push(`<li>${s}</li>`));
        parts.push('</ul>');
      }
      if (data.scope.out_of_scope?.length) {
        parts.push('<p><strong>Out of Scope:</strong></p><ul>');
        data.scope.out_of_scope.forEach(s => parts.push(`<li>${s}</li>`));
        parts.push('</ul>');
      }
    }
    if (data.timeline?.length) {
      parts.push('<h2>Timeline</h2>');
      data.timeline.forEach(p => {
        parts.push(`<p><strong>${p.date_range || 'TBD'}</strong> — ${p.phase_name || 'Phase'}</p>`);
        if (p.focus?.length) {
          parts.push('<ul>');
          p.focus.forEach(f => parts.push(`<li>${f}</li>`));
          parts.push('</ul>');
        }
      });
    }
    if (data.budget_and_resources) {
      parts.push('<h2>Budget & Resources</h2>');
      if (data.budget_and_resources.team_size) {
        let line = `<strong>Team Size:</strong> ${data.budget_and_resources.team_size}`;
        if (data.budget_and_resources.role_breakdown?.length) {
          line += ` (${data.budget_and_resources.role_breakdown.map(r => `${r.count} ${r.role}`).join(', ')})`;
        }
        parts.push(`<p>${line}</p>`);
      }
      if (data.budget_and_resources.year_1_budget) {
        let line = `<strong>Budget:</strong> ${data.budget_and_resources.year_1_budget}`;
        if (data.budget_and_resources.budget_notes) line += ` (${data.budget_and_resources.budget_notes})`;
        parts.push(`<p>${line}</p>`);
      }
    }
    if (data.risks_and_assumptions?.length) {
      parts.push('<h2>Risks & Assumptions</h2><ul>');
      data.risks_and_assumptions.forEach(r => parts.push(`<li>${r}</li>`));
      parts.push('</ul>');
    } else if (data.assumptions?.length) {
      parts.push('<h2>Assumptions</h2><ul>');
      data.assumptions.forEach(a => parts.push(`<li>${a}</li>`));
      parts.push('</ul>');
    }
    if (data.evidence_refs?.length) {
      parts.push('<h2>Key Links</h2><ul>');
      data.evidence_refs.forEach(ref => {
        parts.push(`<li>[${ref.ref_type}] ${ref.ref_id}${ref.note ? ` — ${ref.note}` : ''}</li>`);
      });
      parts.push('</ul>');
    }
    return parts.join('\n');
  }

  // Markdown content → convert to HTML (handles #, ##, ###, bold, lists)
  const cleaned = cleanEmbeddedJson(content);
  const lines = cleaned.split('\n');
  const parts: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const applyInline = (text: string) => text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  const closeList = () => {
    if (listType) {
      parts.push(listType === 'ul' ? '</ul>' : '</ol>');
      listType = null;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      closeList();
      continue;
    }

    // Headings: #, ##, ###
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      closeList();
      const level = headingMatch[1].length;
      const tag = `h${level}`;
      parts.push(`<${tag}>${applyInline(headingMatch[2])}</${tag}>`);
      continue;
    }

    // Unordered list items: - or *
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (listType !== 'ul') {
        closeList();
        parts.push('<ul>');
        listType = 'ul';
      }
      parts.push(`<li>${applyInline(trimmed.slice(2))}</li>`);
      continue;
    }

    // Ordered list items: 1. 2. etc.
    const olMatch = trimmed.match(/^\d+\.\s+(.*)/);
    if (olMatch) {
      if (listType !== 'ol') {
        closeList();
        parts.push('<ol>');
        listType = 'ol';
      }
      parts.push(`<li>${applyInline(olMatch[1])}</li>`);
      continue;
    }

    // Regular paragraph
    closeList();
    parts.push(`<p>${applyInline(trimmed)}</p>`);
  }
  closeList();
  return parts.join('\n');
}

// ─── Section styling helpers ──────────────────────────

function getSectionStyle(title: string) {
  const lower = title.toLowerCase();
  if (lower.includes('key objectives') || lower.includes('objectives'))
    return { boxClass: 'bg-emerald-50/50 border-l-4 border-emerald-500 rounded-r-lg p-5', bulletClass: 'text-emerald-600 font-medium' };
  if (lower.includes('success metrics') || lower.includes('metrics'))
    return { boxClass: 'bg-violet-50/50 border-l-4 border-violet-500 rounded-r-lg p-5', bulletClass: 'text-violet-600 font-medium' };
  if (lower.includes('timeline') || lower.includes('phases'))
    return { boxClass: 'bg-blue-50/50 border-l-4 border-blue-500 rounded-r-lg p-5', bulletClass: 'text-blue-600 font-medium' };
  if (lower.includes('budget') || lower.includes('resources'))
    return { boxClass: 'bg-amber-50/50 border-l-4 border-amber-500 rounded-r-lg p-5', bulletClass: 'text-amber-600 font-medium' };
  if (lower.includes('risks') || lower.includes('assumptions'))
    return { boxClass: 'bg-rose-50/50 border-l-4 border-rose-500 rounded-r-lg p-5', bulletClass: 'text-rose-600 font-medium' };
  return { boxClass: '', bulletClass: 'text-gray-400' };
}

function RenderSection({ title, content }: { title: string; content: string }) {
  const { boxClass, bulletClass } = getSectionStyle(title);
  return (
    <section className="mb-8">
      {title && <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>}
      <div className={boxClass || ''}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4">{children}</h1>,
            h2: ({ children }) => <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3">{children}</h2>,
            h3: ({ children }) => <h3 className="text-base font-semibold text-gray-900 mt-4 mb-2">{children}</h3>,
            p: ({ children }) => <p className="text-gray-600 leading-relaxed mb-3">{children}</p>,
            ul: ({ children }) => <ul className="space-y-2 mb-3">{children}</ul>,
            ol: ({ children }) => <ol className="space-y-2 mb-3 list-decimal list-inside">{children}</ol>,
            li: ({ children }) => (
              <li className="text-gray-700 flex items-start gap-2">
                <span className={bulletClass}>•</span>
                <span>{children}</span>
              </li>
            ),
            strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
            table: ({ children }) => (
              <div className="overflow-x-auto mb-3"><table className="min-w-full border-collapse">{children}</table></div>
            ),
            th: ({ children }) => <th className="border border-gray-200 px-4 py-2 bg-gray-50 font-semibold text-left text-gray-900">{children}</th>,
            td: ({ children }) => <td className="border border-gray-200 px-4 py-2 text-gray-600">{children}</td>,
            hr: () => <hr className="my-6 border-gray-200" />,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </section>
  );
}

// ─── Read-only structured content ─────────────────────

function ReadOnlyStructuredContent({ data }: { data: ProjectOverviewData }) {
  return (
    <div className="space-y-8">
      {data.executive_summary && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Executive Summary</h2>
          <div className="space-y-4 text-gray-600 leading-relaxed">
            {data.executive_summary.paragraph_1 && <p>{data.executive_summary.paragraph_1}</p>}
            {data.executive_summary.paragraph_2 && <p>{data.executive_summary.paragraph_2}</p>}
          </div>
        </section>
      )}
      {data.project_vision && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Vision</h2>
          <p className="text-gray-600 leading-relaxed">{data.project_vision}</p>
        </section>
      )}
      {data.key_objectives && data.key_objectives.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Objectives</h2>
          <div className="bg-emerald-50/50 border-l-4 border-emerald-500 rounded-r-lg p-5">
            <ul className="space-y-2">
              {data.key_objectives.map((objective, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-700">
                  <span className="text-emerald-600 font-medium">•</span>
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
      {data.target_audience && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Target Audience</h2>
          <div className="space-y-4">
            {data.target_audience.primary && (
              <div>
                <p className="text-gray-600"><span className="font-semibold text-gray-900">Primary:</span> {data.target_audience.primary.roles?.join(', ')}</p>
                {data.target_audience.primary.description && <p className="text-gray-500 mt-1">{data.target_audience.primary.description}</p>}
              </div>
            )}
            {data.target_audience.secondary && (
              <div>
                <p className="text-gray-600"><span className="font-semibold text-gray-900">Secondary:</span> {data.target_audience.secondary.roles?.join(', ')}</p>
                {data.target_audience.secondary.description && <p className="text-gray-500 mt-1">{data.target_audience.secondary.description}</p>}
              </div>
            )}
          </div>
        </section>
      )}
      {data.success_metrics && data.success_metrics.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Success Metrics</h2>
          <div className="bg-violet-50/50 border-l-4 border-violet-500 rounded-r-lg p-5">
            <ol className="space-y-3">
              {data.success_metrics.map((metric, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-sm font-medium flex items-center justify-center">{index + 1}</span>
                  <div>
                    <span className="font-semibold text-gray-900">{metric.metric_name}:</span>{' '}
                    <span className="text-gray-600">{metric.target_value || 'TBD'}</span>
                    {metric.measurement_window && <span className="text-gray-500"> within {metric.measurement_window}</span>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}
      {data.timeline && data.timeline.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
          <div className="space-y-4">
            {data.timeline.map((phase, index) => (
              <div key={index} className="flex gap-6">
                <div className="w-24 flex-shrink-0"><span className="text-indigo-600 font-medium">{phase.date_range || 'TBD'}</span></div>
                <div>
                  <h3 className="font-semibold text-gray-900">{phase.phase_name}</h3>
                  {phase.focus && phase.focus.length > 0 && <p className="text-gray-600 mt-1">{phase.focus.join(', ')}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {data.budget_and_resources && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Budget & Resources</h2>
          <div className="space-y-3 text-gray-600">
            {data.budget_and_resources.team_size && (
              <p>
                <span className="font-semibold text-gray-900">Team Size:</span> {data.budget_and_resources.team_size}
                {data.budget_and_resources.role_breakdown && data.budget_and_resources.role_breakdown.length > 0 && (
                  <span className="text-gray-500"> ({data.budget_and_resources.role_breakdown.map(r => `${r.count} ${r.role}`).join(', ')})</span>
                )}
              </p>
            )}
            {data.budget_and_resources.year_1_budget && (
              <p>
                <span className="font-semibold text-gray-900">Budget:</span> {data.budget_and_resources.year_1_budget}
                {data.budget_and_resources.budget_notes && <span className="text-gray-500"> ({data.budget_and_resources.budget_notes})</span>}
              </p>
            )}
          </div>
        </section>
      )}
      {(data.risks_and_assumptions || data.assumptions) && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{data.risks_and_assumptions ? 'Risks & Assumptions' : 'Assumptions'}</h2>
          <ul className="space-y-2 text-gray-600">
            {(data.risks_and_assumptions || data.assumptions)?.map((item, index) => (
              <li key={index} className="flex items-start gap-2"><span className="text-gray-400">•</span><span>{item}</span></li>
            ))}
          </ul>
        </section>
      )}
      {data.evidence_refs && data.evidence_refs.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Links</h2>
          <ul className="space-y-2 text-gray-600">
            {data.evidence_refs.map((ref, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-gray-400">•</span>
                <span>[{ref.ref_type}] {ref.ref_id}{ref.note && <span className="text-gray-500"> — {ref.note}</span>}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────

export function ProjectOverviewContent({
  content,
  isEditing = false,
  onContentChange,
  editorKey = 0,
}: ProjectOverviewContentProps) {
  const data = useMemo(() => parseContent(content), [content]);

  // When editing: show a single Lexical rich text editor with all content as HTML
  if (isEditing) {
    const initialHtml = contentToHtml(content);
    return (
      <RichTextEditor
        key={`editable-${editorKey}`}
        initialContent={initialHtml}
        onChange={onContentChange}
        placeholder="Start writing your project overview..."
        editable
        minHeight="150px"
      />
    );
  }

  // Read-only: if content is HTML (e.g. saved from Lexical editor), render with Lexical
  const trimmedContent = content.trim();
  if (trimmedContent.startsWith('<')) {
    return (
      <RichTextEditor
        key="readonly"
        initialContent={content}
        editable={false}
        minHeight="150px"
      />
    );
  }

  // Read-only: structured JSON → styled render
  if (data) {
    return <ReadOnlyStructuredContent data={data} />;
  }

  // Read-only: markdown → styled sections
  const cleanedContent = cleanEmbeddedJson(content);
  const sections = parseMarkdownSections(cleanedContent);
  return (
    <div className="prose prose-gray max-w-none">
      {sections.map((section, index) => (
        <RenderSection key={index} title={section.title} content={section.content} />
      ))}
    </div>
  );
}
